"use client";

import React, { useState, useEffect } from "react";
import packageJson from "../package.json";
import { useLocation } from "react-router-dom";

function withClearCache(Component) {
  function ClearCacheComponent(props) {
    const [isLatestBuildDate, setIsLatestBuildDate] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
      // * Check current localstorage version with package json build
      const currentVersion = localStorage.getItem("appVersion");

      if (currentVersion) {
        //console.log(`Found version ${currentVersion}`);
      }

      if (!currentVersion) {
        localStorage.setItem("appVersion", packageJson.version);
        //console.log("You're running latest version 😎 👍");
        setIsLatestBuildDate(true);
        return;
      }

      // * Check if latest
      const latestVersion = packageJson.version;

      //console.log(`Incoming version ${latestVersion}`);

      if (compareSemVer(currentVersion, latestVersion) >= 0) {
        //console.log("You're running latest version 😎 👍");
        setIsLatestBuildDate(true);
        return;
      }

      //console.log("Web app out of date 😔, reloading cache ⏳");
      localStorage.setItem("appVersion", latestVersion);
      setIsLatestBuildDate(false);
      refreshCacheAndReload();
    }, [pathname, isLatestBuildDate]);

    function compareSemVer(version1, version2) {
      const semVerRegex =
        /^(\d+)\.(\d+)\.(\d+)(?:-([\da-zA-Z-]+(?:\.[\da-zA-Z-]+)*))?(?:\+[\da-zA-Z-]+(?:\.[\da-zA-Z-]+)*)?$/;

      function parseVersion(version) {
        const match = version.match(semVerRegex);
        if (!match) {
          throw new Error(`Invalid SemVer string: ${version}`);
        }
        return {
          major: parseInt(match[1], 10),
          minor: parseInt(match[2], 10),
          patch: parseInt(match[3], 10),
          preRelease: match[4] ? match[4].split(".") : [],
        };
      }

      function compareIdentifiers(a, b) {
        const isANumeric = /^\d+$/.test(a);
        const isBNumeric = /^\d+$/.test(b);

        if (isANumeric && isBNumeric) {
          return parseInt(a, 10) - parseInt(b, 10);
        }

        if (isANumeric) return -1;
        if (isBNumeric) return 1;

        return a.localeCompare(b);
      }

      function comparePreRelease(preRelease1, preRelease2) {
        const length = Math.max(preRelease1.length, preRelease2.length);
        for (let i = 0; i < length; i++) {
          const id1 = preRelease1[i] || "";
          const id2 = preRelease2[i] || "";
          const result = compareIdentifiers(id1, id2);
          if (result !== 0) return result;
        }
        return 0;
      }

      const v1 = parseVersion(version1);
      const v2 = parseVersion(version2);

      if (v1.major !== v2.major) return v1.major - v2.major;
      if (v1.minor !== v2.minor) return v1.minor - v2.minor;
      if (v1.patch !== v2.patch) return v1.patch - v2.patch;

      if (!v1.preRelease.length && v2.preRelease.length) return 1;
      if (v1.preRelease.length && !v2.preRelease.length) return -1;

      return comparePreRelease(v1.preRelease, v2.preRelease);
    }

    const refreshCacheAndReload = () => {
      if (caches) {
        // Service worker cache should be cleared with caches.delete()
        caches.keys().then((names) => {
          for (const name of names) {
            caches.delete(name);
          }
        });
      }
      // delete browser cache and hard reload
      window.location.reload();
    };

    return (
      <React.Fragment>
        {isLatestBuildDate ? (
          <Component {...props}>{props.children}</Component>
        ) : null}
      </React.Fragment>
    );
  }

  return ClearCacheComponent;
}

export default withClearCache;
